import { ArrowLeft, Calendar, Heart, Info, Watch } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const HRVDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", hrv: 42 },
    { day: "Tue", hrv: 38 },
    { day: "Wed", hrv: 45 },
    { day: "Thu", hrv: 41 },
    { day: "Fri", hrv: 48 },
    { day: "Sat", hrv: 52 },
    { day: "Sun", hrv: 47 },
  ];

  const monthlyData = [
    { week: "Week 1", hrv: 40 },
    { week: "Week 2", hrv: 43 },
    { week: "Week 3", hrv: 46 },
    { week: "Week 4", hrv: 45 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Heart Rate Variability
          </h1>
        </div>

        {/* Sleep Tracking Notice */}
        <Card className="border-warning/50 bg-warning/10 rounded-xl border-2 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-warning/20 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg">
              <Watch className="text-warning h-5 w-5" />
            </div>
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">
                Sleep Tracking Required
              </h4>
              <p className="text-xs text-muted-foreground">
                HRV is recorded only when you wear your tracker watch while
                sleeping. Make sure to wear it overnight for accurate readings.
              </p>
            </div>
          </div>
        </Card>

        {/* Current HRV */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="bg-chart-5/10 flex h-16 w-16 items-center justify-center rounded-xl">
              <Heart className="text-chart-5 h-8 w-8" />
            </div>
            <div>
              <p className="mb-1 text-sm text-muted-foreground">Current HRV</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-foreground">47</span>
                <span className="text-lg text-muted-foreground">ms</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Graphs */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-2">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="day"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hrv"
                      stroke="hsl(var(--chart-5))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--chart-5))" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="monthly">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="week"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hrv"
                      stroke="hsl(var(--chart-5))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--chart-5))" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* What is HRV */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">What is HRV?</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">
                Heart Rate Variability (HRV)
              </strong>{" "}
              measures the variation in time between each heartbeat. It's
              controlled by your autonomic nervous system and reflects your
              body's ability to adapt to stress.
            </p>
            <p>
              A <strong className="text-foreground">higher HRV</strong>{" "}
              generally indicates better cardiovascular fitness, stress
              resilience, and recovery capacity. It suggests your body can
              efficiently switch between "fight or flight" and "rest and digest"
              modes.
            </p>
            <p>
              A <strong className="text-foreground">lower HRV</strong> may
              indicate stress, fatigue, overtraining, or illness. Factors like
              sleep quality, hydration, alcohol consumption, and exercise
              intensity can affect your HRV.
            </p>
          </div>
        </Card>

        {/* Tips */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">
            How to Improve HRV
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-success">•</span>
              <span>Get consistent, quality sleep (7-9 hours)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success">•</span>
              <span>Practice deep breathing and meditation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success">•</span>
              <span>Stay hydrated throughout the day</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success">•</span>
              <span>Avoid alcohol before bed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success">•</span>
              <span>Balance intense workouts with recovery days</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default HRVDetail;
